import { Injectable } from '@angular/core';
import { ILegCurrentStatusViewModel, ILegDetailsViewModel } from '../model/Vendor.model';
import { IAddListnerToInfowindowModle, IDistanceDuration, LegStatusNames } from '../ReusableComponent/map/model/map.model';
import { MapConstants } from '../ReusableComponent/map/model/map-constants.enum';
import { TransportType } from '../@shared/constants/Constant';
declare var markerClusterer: any;

@Injectable({
  providedIn: 'root',
})
export class MapMarkerService {
  private _distanceAndDuration: IDistanceDuration;

  constructor() {}

  private addInfoWindow(mapInstance: google.maps.Map, content: string, marker: google.maps.Marker): google.maps.InfoWindow {
    const infoWindowForMarker = new google.maps.InfoWindow({
      content: content,
    });
    marker.addListener('click', () => {
      infoWindowForMarker.open(mapInstance, marker);
    });
    return infoWindowForMarker;
  }

  createMarker(
    mapInstance: google.maps.Map,
    latLng: google.maps.LatLng,
    title?: string,
    infoWindow?: IAddListnerToInfowindowModle,
    customMarkerImage?: string | object,
  ): google.maps.Marker {
    let marker: google.maps.Marker;
    let infowindow: google.maps.InfoWindow;
    if (!customMarkerImage || typeof customMarkerImage == typeof String()) {
      marker = new google.maps.Marker({
        position: latLng,
        map: mapInstance,
        title: title,
        icon: customMarkerImage ? (customMarkerImage as string) : null,
      });
    }
    if (customMarkerImage && typeof customMarkerImage == typeof Object()) {
      marker = new google.maps.Marker({
        position: latLng,
        map: mapInstance,
        title: title,
        icon: customMarkerImage ? (customMarkerImage as google.maps.Icon) : null,
      });
    }
    if (infoWindow) {
      infowindow = this.addInfoWindow(mapInstance, infoWindow.infoWindowContent, marker);
    }
    if (infoWindow && infoWindow.isAddEventListner) {
      google.maps.event.addListener(infowindow, 'domready', infoWindow.function);
    }

    return marker;
  }

  useMarkerClusterer(mapInstance: google.maps.Map, markers: google.maps.Marker[]) {
    return new markerClusterer.MarkerClusterer({ map: mapInstance, markers: markers });
  }

  getVehicleImage(transportType: string, isCode3: boolean | null, legStatus: string): string {
    if (transportType == TransportType.AIR) {
      return '../../assets/img/plane_image.png';
    } else {
      switch (legStatus) {
        case LegStatusNames.ENROUTE:
          return isCode3 == true ? '../../assets/img/Enroute_Sirun.png' : '../../assets/img/Enroute_Normal.png';
        case LegStatusNames.DRIVERDONE:
          return isCode3 == true ? '../../assets/img/DriverDone_Sirun.png' : '../../assets/img/DriverDone_Normal.png';
        case LegStatusNames.DROPPEDOFF:
          return isCode3 == true ? '../../assets/img/DroppedOff_Sirun.png' : '../../assets/img/DroppedOff_Normal.png';
        case LegStatusNames.ONSCENE:
          return isCode3 == true ? '../../assets/img/OnScene_Sirun.png' : '../../assets/img/OnScene_Normal.png';
        case LegStatusNames.ENROUTE:
          return isCode3 == true ? '../../assets/img/PickedUp_Sirun.png' : '../../assets/img/PickedUp_Normal.png';
        default:
          return isCode3 == true ? '../../assets/img/Sirun_Ambulance.png' : '../../assets/img/Normal_Ambulance.png';
      }
    }
  }

  directionFinder(
    mapInstance: google.maps.Map,
    originLatLng: google.maps.LatLng,
    destinationLatLng: google.maps.LatLng,
    transportType: string,
    strokeColor?: string,
  ) {
    if (transportType == 'Air') {
      const line = new google.maps.Polyline({
        path: [originLatLng, destinationLatLng],
        map: mapInstance,
        geodesic: true,
      });
    } else if (transportType == 'Ground') {
      const waypts: google.maps.DirectionsWaypoint[] = [];
      const directionsService = new google.maps.DirectionsService();
      const directionsDisplay = new google.maps.DirectionsRenderer();
      const method = 'DRIVING';
      directionsDisplay.setMap(mapInstance);
      if (strokeColor) {
        directionsDisplay.setOptions({
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: strokeColor, // Set the stroke color
            strokeOpacity: 1, // Set the stroke opacity (0.0 - 1.0)
            strokeWeight: 3, // Set the stroke weight
          },
        });
      } else {
        directionsDisplay.setOptions({ suppressMarkers: true });
      }
      directionsService.route(
        {
          origin: originLatLng,
          destination: destinationLatLng,
          waypoints: waypts,
          travelMode: google.maps.TravelMode[method],
        },
        function (response, status) {
          if (status === 'OK') {
            directionsDisplay.setDirections(response);
          } else {
          }
        },
      );
    }
  }

  async getDistanceAndDuration(
    originMarker: google.maps.Marker,
    destinationMarker: google.maps.Marker,
    transportType: string,
  ): Promise<IDistanceDuration> {
    if (transportType === TransportType.AIR) {
      return await new Promise((resolve) => {
        const distance = this.getFlightDistance(originMarker, destinationMarker);
        const flightDistance = distance.toFixed(2) + ' miles';
        const duration = (distance / 575).toFixed(2).toString().split('.');
        const flightDuration = duration[0] + ' hours ' + (parseFloat(duration[1]) * 0.6).toFixed(0) + ' mins';
        this._distanceAndDuration = {
          distance: flightDistance,
          duration: flightDuration,
        };
        resolve(this._distanceAndDuration);
      });
    } else if (transportType === TransportType.GROUND) {
      const method = 'DRIVING';
      const service = new google.maps.DistanceMatrixService();
      return await new Promise((resolve) =>
        service.getDistanceMatrix(
          {
            origins: [originMarker.getPosition()],
            destinations: [destinationMarker.getPosition()],
            travelMode: google.maps.TravelMode[method],
          },
          (response, status) => {
            if (status === 'OK') {
              const origins = response.originAddresses;
              let groundDistance;
              let groundDuration;
              for (let i = 0; i < origins.length; i++) {
                const results = response.rows[i].elements;
                for (let j = 0; j < results.length; j++) {
                  const element = results[j];
                  if (element.status !== 'OK' || !element.distance || !element.duration) {
                    continue;
                  }
                  const dist = element.distance.text.split(' ')[0];
                  groundDistance = (parseFloat(dist.replace(/,/g, '')) / 1.609).toFixed(1) + ' miles';
                  groundDuration = element.duration.text;
                }
              }
              this._distanceAndDuration = {
                distance: groundDistance,
                duration: groundDuration,
              };
            }
            resolve(this._distanceAndDuration);
          },
        ),
      );
    } else {
      return Promise.reject(new Error(`Unknown transport type: ${transportType}`));
    }
  }

  getDrivingDurationMinutes(origin: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral): Promise<number | null> {
    const service = new google.maps.DistanceMatrixService();
    return new Promise((resolve) =>
      service.getDistanceMatrix(
        { origins: [origin], destinations: [destination], travelMode: google.maps.TravelMode.DRIVING },
        (response, status) => {
          const element = status === 'OK' ? response?.rows?.[0]?.elements?.[0] : undefined;
          resolve(element?.status === 'OK' && element.duration ? element.duration.value / 60 : null);
        },
      ),
    );
  }

  getFlightDistance(originMarker: google.maps.Marker, destinationMarker: google.maps.Marker): number {
    const radius = 3958.8; // Radius of the Earth in miles
    const originPosition = originMarker?.getPosition();
    const destinationPosition = destinationMarker?.getPosition();
    if (!originPosition || !destinationPosition) {
      return 0;
    }
    const originLattitudeInRadian = originPosition.lat() * (Math.PI / 180); // Convert degrees to radians
    const destinationLattitudeInRadian = destinationPosition.lat() * (Math.PI / 180); // Convert degrees to radians
    const latitudeDifference = destinationLattitudeInRadian - originLattitudeInRadian; // Radian difference (latitudes)
    const logitudeDifference = (destinationPosition.lng() - originPosition.lng()) * (Math.PI / 180); // Radian difference (longitudes)
    const distance =
      2 *
      radius *
      Math.asin(
        Math.sqrt(
          Math.sin(latitudeDifference / 2) * Math.sin(latitudeDifference / 2) +
            Math.cos(originLattitudeInRadian) *
              Math.cos(destinationLattitudeInRadian) *
              Math.sin(logitudeDifference / 2) *
              Math.sin(logitudeDifference / 2),
        ),
      );
    return distance;
  }

  getDriverInfoContent(legDetail: ILegDetailsViewModel | ILegCurrentStatusViewModel) {
    const infoContent = `
      <div><b>Trip: ${legDetail.tripId}</b></div>
      <div class='mt-1'><b>Origin: ${legDetail.origin}</b></div>
      <div class='mt-1'><b>Destination: ${legDetail.destination}</b></div>
      <div class='mt-1'><b>Driver Name: ${legDetail.driverName}</b></div>
      <div class='mt-1'><b>Vehicle Number: ${legDetail.vehicleNumber}</b></div>
    `;
    return infoContent;
  }

  getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';

    // Function to calculate brightness of a color
    function calculateBrightness(color: string) {
      const hexColor = color.substring(1); // Remove the leading '#'
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    }

    // Check if the color is too light
    function isTooLight(color: string) {
      const brightnessThreshold = 160; // Adjust this threshold as per your preference
      return calculateBrightness(color) > brightnessThreshold;
    }

    // Generate a random color and ensure it is not too light
    do {
      color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
    } while (isTooLight(color));

    return color;
  }

  // Generate random unique colors for map routes
  generateUniqueRouteColors(count: number): string[] {
    const usedColors = new Set<string>();
    const routeColors: string[] = [];

    for (let i = 0; i < count; i++) {
      let color: string = this.getRandomColor();

      // Ensure color uniqueness
      while (usedColors.has(color)) {
        color = this.getRandomColor();
      }

      usedColors.add(color);
      routeColors.push(color);
    }

    return routeColors;
  }

  createGeoFence(mapInstance: google.maps.Map, coordinates: google.maps.LatLng, transportType: string) {
    new google.maps.Circle({
      strokeColor: '#FF0000',
      strokeOpacity: 0.5,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.25,
      map: mapInstance,
      center: { lat: coordinates.lat(), lng: coordinates.lng() },
      radius: transportType == TransportType.GROUND ? MapConstants.FENCE_RADIUS_GROUND : MapConstants.FENCE_RADIUS_AIR,
    });
  }
}
